import React, { useState, useEffect, useRef } from "react";

import { 
  Campaign, 
  Letter,
} from "@dndModels";

import DandDAPI from "@utilities/smart_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CharConnectUtilities from "@dndUtilities/char_connect_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";
import ArchiveButton from "@components/input/buttons/icon_buttons/ArchiveButton";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import FlexBox from "@components/layout/FlexBox";
import EditableLabel from "@components/input/strings/EditableLabel";
import CalendarBox from "@components/input/calendar/CalendarBox";
import StringBox from "@components/input/strings/StringBox";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";
import { dateString } from "@dndModels/Calendars";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CommonUtilities from "@utilities/common_utilities";

interface Props {
  campaign: Campaign;
  letter: Letter;
  onDelete: () => void;
  onArchive: () => void;
  autoOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const api = DandDAPI.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();
const common_util = CommonUtilities.getInstance();

export default function LetterInput({ campaign, letter, onDelete, onArchive, autoOpen, onOpenChange }: Props) {
  const [reloading, setReloading] = useState(false);
  const [edit_mode, setEditMode] = useState(false);
  const [ping, setPing] = useState(false);
  const [display_mode, setDisplayMode] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  // Auto-open on mount if the URL points at this letter — mirrors the same
  // owner-vs-viewer mode choice as the click handler below.
  useEffect(() => {
    if (autoOpen) {
      setDisplayMode(true);
      onOpenChange?.(true);
    }
    // Mount-only: don't want this refiring if autoOpen flips true later
    // while the user is already interacting with the modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
        marginBottom: '4px',
      },
    });
  });

  const { classes, cx } = useStyles();

  const openEditMode = () => {
    setEditMode(true);
    onOpenChange?.(true);
  };

  const closeEditMode = () => {
    setEditMode(false);
    onOpenChange?.(false);
  };

  const openDisplayMode = () => {
    setDisplayMode(true);
    onOpenChange?.(true);
  };

  const closeDisplayMode = () => {
    setDisplayMode(false);
    onOpenChange?.(false);
  };

  const renderExtras = () => {
    return (
      <>
        <ModalOrDrawer
          open={edit_mode}
          full_page
          onClose={closeEditMode}>
          <FlexBox direction="column">
            <FlexBox className="header centerd">Letter</FlexBox>
            <FlexBox>
              <EditableLabel
                name="Title"
                value={letter.name}
                onChange={(changed) => {
                  letter.name = changed;
                  api.upsertObject("campaign_letter", letter);
                  setPing(!ping);
                }}
              />
            </FlexBox>
            <FlexBox direction="row">
              <FlexBox>
                <EditableLabel
                  name="From"
                  value={letter.from}
                  onChange={(changed) => {
                    letter.from = changed;
                    api.upsertObject("campaign_letter", letter);
                    setPing(!ping);
                  }}
                />
              </FlexBox>
              <FlexBox>
                <EditableLabel
                  name="To"
                  value={letter.to}
                  onChange={(changed) => {
                    letter.to = changed;
                    api.upsertObject("campaign_letter", letter);
                    setPing(!ping);
                  }}
                />
              </FlexBox>
              <FlexBox>
                <CalendarBox
                  name="Sent"
                  calendar_id={campaign.calendar_id}
                  value={letter.sent}
                  today={campaign.current_date}
                  small
                  onChange={(changed) => {
                    letter.sent = changed;
                    api.upsertObject("campaign_letter", letter);
                    setPing(!ping);
                  }}
                />
              </FlexBox>
            </FlexBox>
            <FlexBox>
              <StringBox
                name="Body"
                multiline
                value={letter.body}
                onBlur={(changed: string) => {
                  letter.body = changed;
                  api.upsertObject("campaign_letter", letter);
                  setPing(!ping);
                }}
              />
            </FlexBox>
          </FlexBox>
        </ModalOrDrawer>
        <ModalOrDrawer
          open={display_mode}
          full_page
          onClose={closeDisplayMode}>
          { renderDisplayMode() }
        </ModalOrDrawer>
      </>
    )
  }

  const renderDisplayMode = () => {
    let char_str = campaign.party_name + " Member";
    const chars = campaign.characters.filter(o => o.owner_id === api.user_id);
    if (chars.length > 0) {
      char_str = common_util.customJoin({ arr: chars.map(o => o.name), separator: ", ", lastSeparator: ", and ", onlySeparator: " and " });
    }
    const renderTo = () => {
      let to = letter.to;
      if (to === "{char}") {
        to = char_str;
      }
      return (
        <FlexBox>
          To: {to}
        </FlexBox>
      );
    }

    return (
      <div className="parchment-wrapper">
        <FlexBox direction="column" className="parchment-scroll double-padded">
          <FlexBox direction="column" className="header">
            <FlexBox>From: {letter.from}</FlexBox>
              {renderTo()}
            <FlexBox>
              {campaign.calendar_id !== "" && letter.sent > 0 ? dateString(letter.sent, campaign.calendar_id) : ""}
            </FlexBox>
          </FlexBox>
          <FlexBox>
            <DisplayStringWithReplacements display_string={common_util.replaceAll(letter.body, "{char}", char_str)} />
          </FlexBox>
        </FlexBox>
      </div>
    );
  }

  return (
    <div className="padded">
      <FlexBox direction="column" className={cx(classes.root)}>
        <FlexBox direction="row">
          <FlexBox className="header padded clickable" onClick={() => {
            if (api.user_id === campaign.owner_id) {
              openEditMode();
            } else {
              openDisplayMode();
            }
          }}>{ letter.name }</FlexBox>
          { api.user_id === campaign.owner_id && 
            <>
              <FlexBox xs_expand={0}>
                <DandDButtonBox name="Display" onClick={openDisplayMode} />
              </FlexBox>
              <FlexBox xs_expand={0} className="centered">
                <div style={{ float: "right"}}>
                  <ArchiveButton
                    no_fab
                    is_archived={letter.is_archived}
                    onClick={onArchive}
                  />
                  <DeleteButton
                    no_fab
                    obj_name={letter.name}
                    onClick={onDelete}
                  />
                </div>
              </FlexBox>
            </>
          }
        </FlexBox>
      </FlexBox>
      { renderExtras() }
    </div>
  );
}